import { all, call, fork, put, takeLatest, select, delay } from 'redux-saga/effects';
import i18n from 'i18next';
import {
  UserPayload,
  fetchUserListError,
  fetchUserListSuccess,
  fetchUserSuccess,
  fetchUserError,
  saveUserSuccess,
  saveUserError,
  createUserError,
  createUserSuccess,
  removeUserSuccess,
  removeUserError,
  editUserRolesSuccess,
  editUserRolesError,
  assignUserLocationRolesSuccess,
  assignUserLocationRolesError,
  resendUserInviteError,
  resendUserInviteSuccess,
  revokeUserInviteError,
  revokeUserInviteSuccess,
  fetchUserList,
} from '../actions/User';

import {
  FETCH_USER,
  FETCH_USER_LIST,
  SAVE_USER,
  CREATE_USER,
  REMOVE_USER,
  EDIT_USER_ROLES,
  ASSIGN_USER_LOCATION_ROLES,
  RESEND_USER_INVITE,
  REVOKE_USER_INVITE,
} from '../constants/ActionTypes';

import {
  getAccountById,
  getUserById,
  saveUser,
  inviteUser,
  removeUser,
  editAccountRoles,
  saveUserCredentials,
  resendUserInvite,
  revokeUserInvite,
} from '../api/User';
import { selectLoggedUser, selectSelectedItems } from 'selectors';
import {
  showError, clearMessage, showMessage
} from '../actions/Common';
import { CustomAction, ListViewRow } from 'model/state';
import { toUser, fromUser, fromUserToInvite, fromPermissionEnumToString, toUserFromPendingInvite } from 'model/api';
import { MessageType, Location, User } from 'model';
import { editLocationRoles, removeLocationRoles } from 'api/Location';
import {InvalidParameterTranslator} from "../helper";
import { isEmpty } from 'lodash';
import { analytics } from 'utils/Analytics';

const WAIT_FOR_MIGRATION_DELAY_MS = 1000;

function* fetchUserListWorker(action: CustomAction<UserPayload>) {
  const accountId = action.payload?.accountId;
  try {
    if (!accountId) {
      throw Error('Invalid Parameters');
    }
    const account = yield call(getAccountById, accountId, ['users', 'pendingInvites']);
    const users = account.users.map((user: any) => toUser(user));
    const pendingInvites = account.pendingInvites.map((pendingInvite: any) => toUserFromPendingInvite(pendingInvite));

    yield put(fetchUserListSuccess([...users, ...pendingInvites]));
  } catch (err) {
    yield put(fetchUserListError());
    yield put(showError(i18n.t('connection_error'), err));
  }
}

function* fetchUserWorker(action: CustomAction<UserPayload>) {
  const userId = action.payload?.userId;
  try {
    if (!userId) {
      throw Error('Invalid Parameters');
    }
    const user = yield call(getUserById, userId);

    yield put(fetchUserSuccess(toUser(user)));
    yield put(clearMessage());
  } catch (err) {
    yield put(fetchUserError());
    yield put(showError(i18n.t('connection_error'), err));
  }
}

function* saveUserWorker(action: CustomAction<UserPayload>) {
  const user = action.payload?.user;
  const credentials = action.payload?.credentials;
  try {
    if (!user || !user.id) {
      throw Error('Invalid Parameters');
    }
    yield put(clearMessage());
    yield call(saveUser, user.id, fromUser(user));

    if (credentials) {
      yield call(saveUserCredentials, user.id, credentials.oldPassword || '', credentials.password);
    }
    yield put(saveUserSuccess());
    yield put(showMessage({ message: i18n.t('user-saved-title'), description: i18n.t('user-saved-description'), type: MessageType.SUCCESS }));
    const loggedUser: User = yield select(selectLoggedUser());
    analytics.trackUserUpdated(loggedUser?.id || '');
  } catch (err) {
    if (err.response?.status === 400 && !isEmpty(err.response?.data?.invalidProperties)) {
      yield put(showMessage({
        message: i18n.t('error-invalid-parameters'),
        description: InvalidParameterTranslator.create(err.response.data.invalidProperties).getDescription(),
        type: MessageType.ERROR
      }));
    } else if (err.response?.status === 400) {
      yield put(showMessage({ message: i18n.t('error-invalid-parameters'), description: i18n.t('invalid-parameter_password'), type: MessageType.ERROR }));
    } else {
      yield put(showError(i18n.t('connection_error'), err));
    }
    yield put(saveUserError());
  }
}

function* createUserWorker(action: CustomAction<UserPayload>) {
  const user = action.payload?.user;
  try {
    if (!user || !user.account?.id) {
      throw Error('Invalid Parameters');
    }

    yield put(clearMessage());
    yield call(inviteUser, fromUserToInvite(user));

    yield put(createUserSuccess());
    yield put(showMessage({ message: i18n.t('user-invited-title'), description: i18n.t('user-invited-description'), type: MessageType.SUCCESS }));
    yield delay(WAIT_FOR_MIGRATION_DELAY_MS);
    yield put(fetchUserList(user.account.id))
    const loggedUser: User = yield select(selectLoggedUser());
    analytics.trackUserAdded(loggedUser?.id || '');
  } catch (err) {
    if (err.response && err.response.status === 409) {
      yield put(showMessage({ message: i18n.t('invite-user-error-title'), description: i18n.t(err.response.data.message), type: MessageType.ERROR }));
    } else {
      yield put(showError(i18n.t('connection_error'), err));
    }
    yield put(createUserError());
  }
}

function* resendUserInviteWorker(action: CustomAction<UserPayload>) {
  const user = action.payload?.user;
  try {
    if (!user || !user?.account) {
      throw Error('Invalid Paramaters');
    }

    yield put(clearMessage());
    yield call(resendUserInvite, user.email, user.account.id);

    yield put(resendUserInviteSuccess());
    yield put(showMessage({ message: i18n.t('user-invite-resent-title'), description: i18n.t('user-invite-resent-description'), type: MessageType.SUCCESS }));
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(resendUserInviteError());
  }
}

function* revokeUserInviteWorker(action: CustomAction<UserPayload>) {
  const user = action.payload?.user;
  try {
    if (!user || !user?.account) {
      throw Error('Invalid Paramaters');
    }

    yield put(clearMessage());
    yield call(revokeUserInvite, user.email, user.account.id);

    yield put(revokeUserInviteSuccess());
    yield put(showMessage({ message: i18n.t('user-invite-revoked-title'), description: i18n.t('user-invite-revoked-description'), type: MessageType.SUCCESS }));

    yield put(fetchUserList(user.account.id))
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(revokeUserInviteError());
  }
}

function* removeUserWorker(action: CustomAction<UserPayload>) {
  const userId = action.payload?.userId;
  try {
    if (!userId) {
      throw Error('Invalid Parameters');
    }

    yield put(clearMessage());
    yield call(removeUser, userId);

    yield put(removeUserSuccess(userId));
    yield put(showMessage({ message: i18n.t('user-removed-title'), description: i18n.t('user-removed-description'), type: MessageType.SUCCESS }));
    const loggedUser: User = yield select(selectLoggedUser());
    analytics.trackUserRemoved(loggedUser?.id || '');
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(removeUserError());
  }
}

function* editUserRolesWorker(action: CustomAction<UserPayload>) {
  const user = action.payload?.user;
  try {
    if (!user || !user.id || !user.account) {
      throw Error('Invalid Parameters');
    }
    yield put(clearMessage());
    yield call(editAccountRoles, user.account.id, user.id, {
      roles: fromPermissionEnumToString(user.accountRole?.roles || [])
    });

    const locationRoles = user.locationRoles || [];
    const apiCalls = [];
    for (let index = 0; index < locationRoles.length; index++) {
      apiCalls.push(call(editLocationRoles, locationRoles[index].locationId, user.id, {
        roles: fromPermissionEnumToString(locationRoles[index].roles)
      }))
    }
    yield all(apiCalls);

    yield put(editUserRolesSuccess());
    yield put(showMessage({ message: i18n.t('user-saved-title'), description: i18n.t('user-saved-description'), type: MessageType.SUCCESS }));
    const loggedUser: User = yield select(selectLoggedUser());
    analytics.trackUserUpdated(loggedUser?.id || '');
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(editUserRolesError());
  }
}

function* assignUserLocationRolesWorker(action: CustomAction<UserPayload>) {
  const roles = action.payload?.roles;
  const userId = action.payload?.userId;
  try {
    if (!roles || !userId) {
      throw Error('Invalid Parameters');
    }
    yield put(clearMessage());

    const selectedLocations: Array<ListViewRow<Location>> = yield select(selectSelectedItems());
    const apiCalls = [];
    for (let index = 0; index < selectedLocations.length; index++) {
      if (roles.length > 0) {
        apiCalls.push(call(editLocationRoles, selectedLocations[index].id || '', userId, {
          roles: fromPermissionEnumToString(roles)
        }));
      } else {
        apiCalls.push(call(removeLocationRoles, selectedLocations[index].id || '', userId));
      }
    }
    yield all(apiCalls);

    yield put(assignUserLocationRolesSuccess());
    yield put(showMessage({ message: i18n.t('user-location-roles-saved-title'), description: i18n.t('user-location-roles-saved-description'), type: MessageType.SUCCESS }));
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(assignUserLocationRolesError());
  }
}

export function* fetchUserListSaga() {
  yield takeLatest(FETCH_USER_LIST, fetchUserListWorker);
}

export function* fetchUserSaga() {
  yield takeLatest(FETCH_USER, fetchUserWorker);
}

export function* saveUserSaga() {
  yield takeLatest(SAVE_USER, saveUserWorker);
}

export function* createUserSaga() {
  yield takeLatest(CREATE_USER, createUserWorker);
}

export function* resendUserInviteSaga() {
  yield takeLatest(RESEND_USER_INVITE, resendUserInviteWorker);
}

export function* revokeUserInviteSaga() {
  yield takeLatest(REVOKE_USER_INVITE, revokeUserInviteWorker);
}

export function* removeUserSaga() {
  yield takeLatest(REMOVE_USER, removeUserWorker);
}

export function* editUserRolesSaga() {
  yield takeLatest(EDIT_USER_ROLES, editUserRolesWorker);
}

export function* assignUserLocationRolesSaga() {
  yield takeLatest(ASSIGN_USER_LOCATION_ROLES, assignUserLocationRolesWorker);
}

export default function* rootSaga() {
  yield all([
    fork(fetchUserListSaga),
    fork(fetchUserSaga),
    fork(saveUserSaga),
    fork(createUserSaga),
    fork(resendUserInviteSaga),
    fork(revokeUserInviteSaga),
    fork(removeUserSaga),
    fork(editUserRolesSaga),
    fork(assignUserLocationRolesSaga),
  ]);
}
